import React from 'react';
import { moonGatheringsData } from '../data';
import SEO from '../components/SEO';

const moonGatheringsSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": "https://moonyoga-fredericia.dk/moon-gatherings/#webpage",
      "url": "https://moonyoga-fredericia.dk/moon-gatherings",
      "name": "Måneceremoni 🌙 & Moon Gatherings | Moon Yoga Fredericia",
      "description": "Deltag i vores magiske måneceremonier med astrologi, ceremoniel kakao, kundalini og fællesskab i takt med månens cyklus."
    },
    {
      "@type": "EventSeries",
      "name": "Måneceremonier Hos Moon Yoga",
      "description": "Måneceremonier i 2026 med ceremoniel kakao, kundalini meditation, astro talk og fællesskab.",
      "organizer": {
        "@type": "Organization",
        "name": "Moon Yoga Fredericia",
        "url": "https://moonyoga-fredericia.dk"
      }
    }
  ]
};

export default function MoonGatherings() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 space-y-20 sm:space-y-24">
      <SEO
        title="Måneceremoni 🌙 & Moon Gatherings | Moon Yoga Fredericia"
        description="Deltag i vores magiske måneceremonier med astrologi, ceremoniel kakao, kundalini og fællesskab i takt med månens cyklus hos Moon Yoga."
        path="/moon-gatherings"
        schema={moonGatheringsSchema}
      />
      
      {/* Page Header & Breadcrumbs */}
      <div className="space-y-6 text-center max-w-4xl mx-auto">
        <nav className="text-xs tracking-widest uppercase text-brand-muted flex items-center justify-center gap-2" aria-label="Brødkrummer">
          <a href="/" className="hover:text-brand-primary transition-colors">Moon Yoga</a>
          <i className="fa-solid fa-chevron-right text-[8px] text-brand-primary/30" />
          <span className="text-brand-primary font-medium">Moon Gatherings</span>
        </nav>
        
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-primary font-semibold tracking-wide">
          {moonGatheringsData.title}
        </h1>
        
        <p className="text-base sm:text-xl text-brand-muted leading-relaxed whitespace-pre-line max-w-3xl mx-auto">
          {moonGatheringsData.intro}
        </p>
        <div className="inline-block h-1 w-20 bg-brand-primary/30 rounded-full mt-2" />
      </div>

      {/* Ceremoniens Elementer */}
      <section className="max-w-6xl mx-auto space-y-10">
        <div className="text-center space-y-3">
          <span className="text-xs tracking-[0.25em] uppercase text-brand-primary font-semibold block">
            Dybde & Nærvær
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl text-brand-primary font-semibold tracking-wide">
            {moonGatheringsData.elementsTitle}
          </h2>
        </div>

        <div className="flex flex-wrap justify-center gap-8">
          {moonGatheringsData.elements.map((elem, idx) => (
            <div
              key={idx}
              className="w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.35rem)] bg-brand-surface border border-brand-primary/10 rounded-3xl p-7 sm:p-8 space-y-4 shadow-sm hover:border-brand-primary/25 hover:shadow-md transition-all duration-300 text-center flex flex-col items-center"
            >
              <div className="text-4xl mb-2">{elem.icon}</div>
              <h3 className="font-serif text-xl font-semibold text-brand-primary">
                {elem.title}
              </h3>
              <p className="text-sm sm:text-base text-brand-text/90 leading-relaxed">
                {elem.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Afsluttende Beskrivelse */}
      <section className="max-w-5xl mx-auto bg-brand-light-gold/40 border border-brand-primary/15 rounded-3xl p-8 sm:p-12 shadow-sm text-center space-y-6">
        <i className="fa-solid fa-moon text-3xl text-brand-primary/40" aria-hidden="true" />
        <div className="text-brand-text/90 text-base sm:text-lg lg:text-xl leading-relaxed whitespace-pre-line max-w-4xl mx-auto">
          {moonGatheringsData.finalDescription}
        </div>
      </section>

      {/* Datoer & Tidspunkter */}
      <section className="max-w-6xl mx-auto space-y-12">
        <div className="text-center space-y-3 max-w-2xl mx-auto">
          <span className="text-xs tracking-[0.25em] uppercase text-brand-primary font-semibold block">
            Månekalender 2026
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl text-brand-primary font-semibold tracking-wide">
            Datoer & tidspunkter
          </h2>
          <p className="text-base text-brand-muted">
            Giv dig selv gaven at følge månens cyklus i 2026. Book din plads i god tid.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {moonGatheringsData.ceremonies.map((ceremony, index) => {
            const isFullMoon = ceremony.type === "fuldmane";

            return (
              <div
                key={index}
                className="bg-brand-surface border border-brand-primary/15 hover:border-brand-primary/35 rounded-3xl p-6 sm:p-8 flex flex-col justify-between gap-6 shadow-sm hover:shadow-md transition-all duration-300 group"
              >
                {/* Top Row: Date & Moon Badge */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-4 border-b border-brand-primary/10 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-2xl bg-brand-primary/5 border border-brand-primary/10 flex items-center justify-center text-brand-primary font-serif font-bold text-lg">
                        <i className={`fa-solid ${isFullMoon ? 'fa-circle text-amber-600/80' : 'fa-moon text-brand-primary/80'} text-sm`} aria-hidden="true" />
                      </div>
                      <div>
                        <span className="text-xs font-bold tracking-widest uppercase text-brand-muted block">
                          {ceremony.day}
                        </span>
                        <span className="text-base sm:text-lg font-serif font-bold text-brand-primary">
                          {ceremony.date}
                        </span>
                      </div>
                    </div>

                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide border ${
                      isFullMoon 
                        ? 'bg-[#FAF3E1] text-[#6B4E1B] border-[#EAD2A8]' 
                        : 'bg-[#EBF0EA] text-[#384333] border-[#384333]/20'
                    }`}>
                      {isFullMoon ? 'Fuldmåne' : 'Nymåne'}
                    </span>
                  </div>

                  {/* Title & Time */}
                  <div className="space-y-2 pt-1">
                    <h3 className="text-xl sm:text-2xl font-serif font-semibold text-brand-primary tracking-wide">
                      {ceremony.title}
                    </h3>
                    <div className="flex items-center gap-2 text-sm sm:text-base text-brand-text/80 font-medium">
                      <i className="fa-regular fa-clock text-brand-primary/70" aria-hidden="true" />
                      <span>kl. {ceremony.time}</span>
                    </div>
                  </div>
                </div>

                {/* Bottom CTA */}
                <div className="pt-2">
                  <a
                    href={ceremony.link || "https://moonyoga.yogo.dk/frontend/index.html#/"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2.5 w-full py-3 px-6 bg-brand-primary hover:bg-brand-primary-hover text-white text-sm font-semibold rounded-full transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <span>Book plads</span>
                    <i className="fa-solid fa-arrow-right text-xs transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Priser Section */}
      <section className="max-w-4xl mx-auto bg-brand-light-gold/30 border border-brand-primary/15 rounded-3xl p-8 sm:p-12 space-y-8 text-center">
        <div className="space-y-2 max-w-xl mx-auto">
          <span className="text-xs tracking-[0.25em] uppercase text-brand-primary font-semibold block">
            Deltagelse
          </span>
          <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-brand-primary">
            Priser for Måneceremonier
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
          {moonGatheringsData.prices.map((p, pIdx) => (
            <div key={pIdx} className="bg-brand-surface p-6 rounded-2xl border border-brand-primary/10 space-y-2 shadow-sm">
              <span className="block text-xs text-brand-muted uppercase tracking-wider font-semibold">{p.name}</span>
              <span className="text-2xl font-serif font-bold text-brand-primary block">{p.price}</span>
            </div>
          ))}
        </div>

        <div>
          <a
            href="https://moonyoga.yogo.dk/frontend/index.html#/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white text-sm font-semibold tracking-wide shadow-sm hover:shadow-md transition-all duration-300"
          >
            <span>Køb ceremoni / pas i YOGO</span>
            <i className="fa-solid fa-arrow-right text-xs" />
          </a>
        </div>
      </section>

      {/* Andre Events Section */}
      {moonGatheringsData.otherEvents && moonGatheringsData.otherEvents.length > 0 && (
        <section className="max-w-6xl mx-auto space-y-12 pt-6">
          <div className="text-center space-y-3 max-w-2xl mx-auto">
            <span className="text-xs tracking-[0.25em] uppercase text-brand-primary font-semibold block">
              Workshops & Retreats
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl text-brand-primary font-semibold tracking-wide">
              {moonGatheringsData.otherEventsTitle || "Andre Events"}
            </h2>
            <p className="text-base text-brand-muted">
              {moonGatheringsData.otherEventsIntro}
            </p>
          </div>

          {moonGatheringsData.otherEvents.map((event) => (
            <div
              key={event.id}
              className="bg-brand-surface border border-brand-primary/15 rounded-3xl p-6 sm:p-10 space-y-8 shadow-sm"
            >
              {/* Event Main Header */}
              <div className="space-y-4 text-center border-b border-brand-primary/10 pb-8">
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-light-gold/60 border border-brand-primary/20 text-brand-primary text-xs font-semibold uppercase tracking-wider">
                  <i className="fa-solid fa-sparkles text-amber-600" aria-hidden="true" />
                  Special Event
                </span>
                <h3 className="font-serif text-3xl sm:text-4xl font-bold text-brand-primary">
                  {event.title}
                </h3>

                {/* Event Notice Box */}
                {event.notice && (
                  <div className="max-w-lg mx-auto text-center pt-2">
                    <div className="inline-flex flex-col items-center gap-1.5 px-6 py-3.5 rounded-2xl bg-brand-light-gold/40 border border-brand-primary/15 text-brand-primary shadow-xs">
                      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-primary/80">
                        <i className="fa-solid fa-gift text-amber-700" aria-hidden="true" />
                        <span>Information & Goodiebag</span>
                      </div>
                      <div className="leading-relaxed text-xs sm:text-sm font-medium text-brand-primary/90 space-y-1">
                        <p className="flex items-center justify-center gap-1.5 flex-wrap">
                          <span>Der gives 1 goodiebag pr. deltager - ikke pr. dag</span>
                          <i className="fa-regular fa-face-smile-wink text-amber-700" aria-hidden="true" />
                          <i className="fa-solid fa-heart text-rose-600/80 text-xs" aria-hidden="true" />
                        </p>
                        <p className="flex items-center justify-center gap-1.5 flex-wrap">
                          <span>Se programmet under hver enkelt dag</span>
                          <i className="fa-solid fa-heart text-rose-600/80 text-xs" aria-hidden="true" />
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Event Days Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {event.days.map((day) => (
                  <div
                    key={day.dayNumber}
                    className="bg-brand-light-gold/20 border border-brand-primary/10 rounded-2xl p-6 flex flex-col justify-between space-y-5"
                  >
                    <div className="space-y-4">
                      {/* Day Header Badge & Time */}
                      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-brand-primary/10 pb-3">
                        <span className="text-xs font-bold tracking-widest uppercase text-brand-primary bg-brand-primary/10 px-3 py-1 rounded-full">
                          Dag {day.dayNumber}
                        </span>
                        <div className="text-xs font-semibold text-brand-muted flex items-center gap-1.5">
                          <i className="fa-regular fa-calendar-days text-brand-primary/70" />
                          <span>{day.date}</span>
                          <span className="mx-1">•</span>
                          <i className="fa-regular fa-clock text-brand-primary/70" />
                          <span>kl. {day.startTime} - {day.endTime}</span>
                        </div>
                      </div>

                      {/* Day Title */}
                      <h4 className="font-serif text-lg font-bold text-brand-primary leading-snug">
                        {day.title}
                      </h4>

                      {/* Day Intro */}
                      <p className="text-xs sm:text-sm text-brand-text/90 leading-relaxed italic">
                        {day.intro}
                      </p>

                      {/* Day Program List */}
                      <div className="space-y-3 pt-2">
                        <p className="text-xs font-semibold uppercase tracking-wider text-brand-primary border-b border-brand-primary/10 pb-1.5">
                          {day.programTitle}
                        </p>
                        <ul className="space-y-2">
                          {day.programItems.map((item, itemIdx) => (
                            <li key={itemIdx} className="flex items-start gap-2 text-xs text-brand-text/90">
                              <i className="fa-solid fa-leaf text-[10px] text-brand-primary/60 mt-1 flex-shrink-0" aria-hidden="true" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Day Booking Link */}
                    {day.link && (
                      <div className="pt-4 border-t border-brand-primary/10">
                        <a
                          href={day.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white text-xs sm:text-sm font-semibold rounded-xl transition-all duration-200 shadow-xs"
                        >
                          <span>Book plads til Dag {day.dayNumber}</span>
                          <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Event Bottom CTA */}
              <div className="text-center pt-4">
                <a
                  href={event.link || "https://moonyoga.yogo.dk/frontend/index.html?itemType=event&itemId=26546#/login-with-cart"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2.5 py-3.5 px-8 bg-brand-primary hover:bg-brand-primary-hover text-white text-sm font-semibold rounded-full transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <span>Book plads til Gua Sha Godess event i YOGO</span>
                  <i className="fa-solid fa-arrow-right text-xs" />
                </a>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Notice */}
      <section className="text-center max-w-2xl mx-auto p-8 rounded-3xl bg-brand-light-gold/30 border border-brand-primary/10 space-y-3">
        <i className="fa-light fa-sparkles text-brand-primary text-3xl block mx-auto" />
        <p className="text-base text-brand-muted font-medium leading-relaxed">
          {moonGatheringsData.notice}
        </p>
      </section>

    </div>
  );
}
